import { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'localization';
import { fetchPermissionList, patchSphereProperties } from '@center-platform/spheres/sphere';
import type { DataTableSortEvent } from 'primereact/datatable';
import SphereIdentityAtom from '../../atoms/sphere-identity/SphereIdentityAtom';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import EmptyStateAtom from '../../atoms/EmptyState/EmptyStateAtom';
import SwitchAtom from '../../atoms/Switch/SwitchAtom';
import SegmentedControlAtom from '../../atoms/SegmentedControl/SegmentedControlAtom';
import type { SegmentedControlOption } from '../../atoms/SegmentedControl/SegmentedControlAtom';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { usePropertyAccess } from '../../../hooks/usePropertyAccess';
import DialogWrapper from '../../molecules/DialogWrapper/DialogWrapper';
import EditSphere from '../../molecules/Atlas/EditSphere/EditSphere';
import AtlasPerspectiveSelector from '../../molecules/Atlas/AtlasPerspectiveSelector/AtlasPerspectiveSelector';
import PrimeReactSingleSelect, { OptionType } from '../../atoms/single-select-dropdown/single-select-dropdown';
import InviteUsersDialogMolecule from '../../molecules/Users/InviteUsersMolecule';
import DataListMolecule from '../../molecules/DataList/DataListMolecule';
import type { DataGridColumn } from '../../atoms/DataGrid/DataGridAtom';
import './UserListOrganism.scss';

interface AssignedPerspective {
  /** Context execute API format */
  perspectiveSystemName?: string;
  assignedSpheres?: { id: string; [key: string]: any }[];
  /** Legacy surface API format */
  perspectiveProperties?: { system_name: string };
  assigned?: { sphere: { id: string; [key: string]: any } }[];
  [key: string]: any;
}

interface User {
  id: string;
  label: string;
  properties: Record<string, any>;
  assignedPerspectives: AssignedPerspective[];
  [key: string]: any;
}

interface UserListOrganismProps {
  organizationId: string;
}

/** Extract a value that may be stored as an array or a scalar */
function unwrap(val: unknown): string {
  if (Array.isArray(val)) return val[0] ?? '';
  return (val as string) ?? '';
}

/** Determine the active boolean from the mixed storage format */
function isUserActive(user: User): boolean {
  const raw = Array.isArray(user?.properties?.active)
    ? user?.properties?.active?.[0]
    : user?.properties?.active;
  return String(raw) === 'true' || raw === true;
}

/** Find a perspective by system_name (handles both context API and legacy formats) */
function findPerspective(user: User, systemName: string) {
  return user.assignedPerspectives?.find(
    (p) => p?.perspectiveSystemName === systemName || p?.perspectiveProperties?.system_name === systemName
  );
}

/** Get the first assigned sphere from a perspective (handles both formats) */
function getAssignedSphere(user: User, systemName: string) {
  const persp = findPerspective(user, systemName);
  if (!persp) return undefined;
  // Context API format: assignedSpheres[] (spheres directly)
  if (persp.assignedSpheres?.[0]) return persp.assignedSpheres[0];
  // Legacy format: assigned[].sphere
  return persp.assigned?.[0]?.sphere;
}

// ---------- Sub-components (extracted to avoid nested definitions) ----------

interface UserNameCellProps {
  user: User;
  onEdit: (user: User) => void;
}

function UserNameCell({ user, onEdit }: Readonly<UserNameCellProps>) {
  const sphereName = unwrap(user.properties?.name);
  const imageId = unwrap(user.properties?.imageId);
  return (
    <button
      type="button"
      className="user-list-organism__name-link"
      onClick={() => { onEdit(user); }}
    >
      <SphereIdentityAtom
        glyph={user.glyph}
        glyphColor={user.glyphColor}
        imageId={imageId}
        sphereName={sphereName}
        sphereId={user.id}
        size="small"
      />
    </button>
  );
}

interface ActiveStatusToggleProps {
  user: User;
  canToggle: boolean;
  graphClient: any;
  fetchUsers: () => Promise<void>;
}

const ActiveStatusToggle = ({ user, canToggle, graphClient, fetchUsers }: ActiveStatusToggleProps) => {
  const { t } = useTranslation();
  const [switchLoading, setSwitchLoading] = useState(false);
  const isActive = isUserActive(user);
  const userName = unwrap(user.properties?.name) || user.label;

  const handleToggle = async () => {
    if (!canToggle || switchLoading) return;
    setSwitchLoading(true);
    try {
      if (isActive) {
        await graphClient.delete(`/spheres/${user.id}/deactivate-user`);
      } else {
        await graphClient.post(`/spheres/${user.id}/reactivate-user`);
      }
      await fetchUsers();
    } catch (err) {
      console.error('Failed to toggle user status:', err);
    } finally {
      setSwitchLoading(false);
    }
  };

  return (
    <SwitchAtom
      checked={isActive}
      disabled={!canToggle || switchLoading}
      onChange={handleToggle}
      ariaLabel={t('usersMolecule.activeToggle', { name: userName })}
    />
  );
};

interface UserActiveCellProps {
  user: User;
  canToggle: boolean;
  graphClient: any;
  fetchUsers: () => Promise<void>;
}

function UserActiveCell({ user, canToggle, graphClient, fetchUsers }: Readonly<UserActiveCellProps>) {
  return (
    <ActiveStatusToggle
      user={user}
      canToggle={canToggle}
      graphClient={graphClient}
      fetchUsers={fetchUsers}
    />
  );
}

function UserIdentityCell({ user }: Readonly<{ user: User }>) {
  const identity = getAssignedSphere(user, 'center_associated_identity');
  return (
    <SphereIdentityAtom
      glyph={identity?.glyph}
      glyphColor={identity?.glyphColor}
      imageId={unwrap(identity?.properties?.imageId)}
      sphereName={unwrap(identity?.properties?.name)}
      sphereId={identity?.id}
      size="small"
    />
  );
}

interface UserPerspectiveCellProps {
  user: User;
  perspectiveSystemName: string;
  organizationId: string;
  onPerspectiveChange: (sphereId: string, perspectiveSystemName: string, assignee: string | null) => Promise<void>;
}

function UserPerspectiveCell({ user, perspectiveSystemName, organizationId, onPerspectiveChange }: Readonly<UserPerspectiveCellProps>) {
  return (
    <AtlasPerspectiveSelector
      className="no-border-shadow"
      perspectiveSystemName={perspectiveSystemName}
      sphere={user as any}
      organizationId={organizationId}
      isSelected={false}
      activeSection=""
      activeStyles={{}}
      parentNode={undefined}
      onAssigneeSelected={async (assignee, sphereId, psn) => {
        await onPerspectiveChange(sphereId, psn, assignee);
      }}
    />
  );
}

interface MobileUserCardProps {
  user: User;
  canToggle: boolean;
  graphClient: any;
  fetchUsers: () => Promise<void>;
  onEditClick: (user: User) => void;
  organizationId: string;
  onPerspectiveChange: (sphereId: string, perspectiveSystemName: string, assignee: string | null) => Promise<void>;
}

const MobileUserCard = ({
  user,
  canToggle,
  graphClient,
  fetchUsers,
  onEditClick,
  organizationId,
  onPerspectiveChange
}: MobileUserCardProps) => {
  const { t } = useTranslation();

  const sphereName = unwrap(user.properties?.name);
  const imageId = unwrap(user.properties?.imageId);
  const title = unwrap(user.properties?.title);
  const identity = getAssignedSphere(user, 'center_associated_identity');

  return (
    <div className="user-list-organism__mobile-card">
      <div className="user-list-organism__mobile-card-header">
        <div className="user-list-organism__mobile-user-info">
          <div className="user-list-organism__mobile-avatar">
            <SphereIdentityAtom
              glyph={user.glyph}
              glyphColor={user.glyphColor}
              imageId={imageId}
              sphereName={sphereName}
              sphereId={user.id}
              size="medium"
            />
          </div>
          <div className="user-list-organism__mobile-name-title">
            <button
              type="button"
              className="user-list-organism__mobile-name"
              onClick={(e) => { e.stopPropagation(); onEditClick(user); }}
            >
              {sphereName}
            </button>
            {title && <div className="user-list-organism__mobile-title">{title}</div>}
          </div>
        </div>
        <ActiveStatusToggle
          user={user}
          canToggle={canToggle}
          graphClient={graphClient}
          fetchUsers={fetchUsers}
        />
      </div>

      <div className="user-list-organism__mobile-card-body">
        <div className="user-list-organism__mobile-detail-row">
          <span className="user-list-organism__mobile-detail-label">{t('usersMolecule.identity')}:</span>
          <div className="user-list-organism__mobile-detail-value">
            <SphereIdentityAtom
              glyph={identity?.glyph}
              glyphColor={identity?.glyphColor}
              imageId={unwrap(identity?.properties?.imageId)}
              sphereName={unwrap(identity?.properties?.name)}
              sphereId={identity?.id}
              size="small"
            />
          </div>
        </div>

        <div className="user-list-organism__mobile-detail-row">
          <span className="user-list-organism__mobile-detail-label">{t('usersMolecule.center')}:</span>
          <div className="user-list-organism__mobile-detail-value">
            <AtlasPerspectiveSelector
              className="no-border-shadow user-list-organism__mobile-selector"
              perspectiveSystemName="center_center"
              sphere={user as any}
              organizationId={organizationId}
              isSelected={false}
              activeSection=""
              activeStyles={{}}
              parentNode={undefined}
              onAssigneeSelected={async (assignee, sphereId, perspectiveSystemName) => {
                await onPerspectiveChange(sphereId, perspectiveSystemName, assignee);
              }}
            />
          </div>
        </div>

        <div className="user-list-organism__mobile-detail-row">
          <span className="user-list-organism__mobile-detail-label">{t('usersMolecule.manager')}:</span>
          <div className="user-list-organism__mobile-detail-value">
            <AtlasPerspectiveSelector
              className="no-border-shadow user-list-organism__mobile-selector"
              perspectiveSystemName="center_manager"
              sphere={user as any}
              organizationId={organizationId}
              isSelected={false}
              activeSection=""
              activeStyles={{}}
              parentNode={undefined}
              onAssigneeSelected={async (assignee, sphereId, perspectiveSystemName) => {
                await onPerspectiveChange(sphereId, perspectiveSystemName, assignee);
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// Property descriptor used by usePropertyAccess to gate user_type writes.
const USER_TYPE_PROP = { access: { write: 'manage_security' } } as const;

// ---------- RoleEditSection ----------

interface RoleEditSectionProps {
  sphere: User;
  organizationId: string;
  graphClient: any;
  onSaved: () => Promise<void>;
}

function RoleEditSection({ sphere, organizationId, graphClient, onSaved }: Readonly<RoleEditSectionProps>) {
  const { t } = useTranslation();
  const { canWrite, loading: accessLoading } = usePropertyAccess(organizationId, USER_TYPE_PROP);

  const currentType = (unwrap(sphere.properties?.user_type) || 'user') as 'user' | 'admin';
  const [roleValue, setRoleValue] = useState<'user' | 'admin'>(currentType);
  const [saving, setSaving] = useState(false);

  const options: SegmentedControlOption<'user' | 'admin'>[] = [
    { value: 'user', label: t('usersMolecule.userRole.user'), disabled: !canWrite },
    { value: 'admin', label: t('usersMolecule.userRole.admin'), disabled: !canWrite },
  ];

  const handleRoleChange = useCallback(async (newVal: 'user' | 'admin' | ('user' | 'admin')[]) => {
    const val = Array.isArray(newVal) ? newVal[0] : newVal;
    if (!val || val === roleValue || !canWrite || saving) return;
    setSaving(true);
    try {
      await patchSphereProperties(graphClient, { sphereId: sphere.id, properties: { user_type: val } });
      setRoleValue(val);
      await onSaved();
    } catch (_err) {
      // value remains unchanged on error
    } finally {
      setSaving(false);
    }
  }, [roleValue, canWrite, saving, graphClient, sphere.id, onSaved]);

  return (
    <div className="user-list-organism__role-edit">
      <label className="user-list-organism__role-edit-label">
        {t('usersMolecule.role')}
      </label>
      <div className="user-list-organism__role-edit-control">
        <SegmentedControlAtom
          options={options}
          value={roleValue}
          onChange={(v) => { handleRoleChange(v as 'user' | 'admin' | ('user' | 'admin')[]); }}
          mode="single"
          ariaLabel={t('usersMolecule.role')}
          data-testid="role-segmented-control"
        />
        {!canWrite && !accessLoading && (
          <span
            className="user-list-organism__role-lock pi pi-lock"
            title={t('usersMolecule.roleWriteDeniedTooltip')}
            data-testid="role-lock-icon"
          />
        )}
      </div>
    </div>
  );
}

// ---------- Column body factory functions (extracted to avoid closures inside render) ----------

function createUserNameBody(onEdit: (user: User) => void) {
  return (row: User) => <UserNameCell user={row} onEdit={onEdit} />;
}

function createTitleBody() {
  return (row: User) => unwrap(row.properties?.title);
}

function createActiveBody(canToggle: boolean, graphClient: any, fetchUsers: () => Promise<void>) {
  return (row: User) => <UserActiveCell user={row} canToggle={canToggle} graphClient={graphClient} fetchUsers={fetchUsers} />;
}

function createIdentityBody() {
  return (row: User) => <UserIdentityCell user={row} />;
}

function createPerspectiveBody(
  perspectiveSystemName: string,
  organizationId: string,
  onPerspectiveChange: (sphereId: string, perspectiveSystemName: string, assignee: string | null) => Promise<void>
) {
  return (row: User) => (
    <UserPerspectiveCell
      user={row}
      perspectiveSystemName={perspectiveSystemName}
      organizationId={organizationId}
      onPerspectiveChange={onPerspectiveChange}
    />
  );
}

function createRoleBody(t: (key: string) => string) {
  return (row: User) => {
    const userType = unwrap(row.properties?.user_type) || 'user';
    const isAdmin = userType === 'admin';
    return (
      <ChipAtom
        label={isAdmin ? t('usersMolecule.userRole.admin') : t('usersMolecule.userRole.user')}
        className={isAdmin ? 'user-list-organism__role-chip--admin' : 'user-list-organism__role-chip--user'}
      />
    );
  };
}

// ---------- Main Component ----------

const UserListOrganism: React.FC<UserListOrganismProps> = ({ organizationId }) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();

  // Data state
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);

  // Filter state
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState<OptionType>({ name: t('usersMolecule.status.all'), code: 'all' });
  const [centerFilter, setCenterFilter] = useState<OptionType>({ name: t('usersMolecule.status.all'), code: 'all' });
  const [centerOptions, setCenterOptions] = useState<OptionType[]>([{ name: t('usersMolecule.status.all'), code: 'all' }]);

  // Sort state
  const [sortField, setSortField] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<1 | -1>(1);

  // Dialog state
  const [selectedSphere, setSelectedSphere] = useState<any>(null);
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [inviteDialogVisible, setInviteDialogVisible] = useState(false);

  // Permission state
  const [permissionList, setPermissionList] = useState<{ permissions: any }>({ permissions: {} });

  const graphClient = apiClients?.graphClient;
  const permissionClient = apiClients?.permissionClient;
  const canManageSecurity = !!permissionList?.permissions?.manage_security;

  const statusOptions: OptionType[] = useMemo(() => [
    { name: t('usersMolecule.status.all'), code: 'all' },
    { name: t('usersMolecule.status.active'), code: 'active' },
    { name: t('usersMolecule.status.inactive'), code: 'inactive' },
  ], [t]);

  // ---------- Data fetching ----------

  // `quiet` re-reads in the background: no loading state, and a failure keeps the rows on screen.
  const loadUsers = useCallback(async (quiet: boolean) => {
    if (!graphClient) return;
    if (!quiet) {
      setLoading(true);
      setLoadFailed(false);
    }
    try {
      const data = await graphClient.get('/contexts/center_user_management_context/execute', {
        params: { rootSphereId: organizationId, responseMode: 'tree' },
      });
      const roots = data?.roots ?? [];
      const usersList: User[] = roots.length > 0 ? (roots[0].children ?? []) : [];
      setUsers(usersList);
    } catch (error) {
      if (!quiet) {
        setUsers([]);
        setLoadFailed(true);
      }
      console.error('Failed to fetch users:', error);
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [graphClient, organizationId]);

  const fetchUsers = useCallback(() => loadUsers(false), [loadUsers]);

  // A user changed while its editor stays open (its glyph, colour or picture).
  const handleSphereRefresh = useCallback(() => {
    void loadUsers(true);
  }, [loadUsers]);

  useEffect(() => {
    const fetchPermissions = async () => {
      if (!organizationId || !permissionClient) return;
      const list = await fetchPermissionList(permissionClient, {
        resourceType: 'center',
        resourceId: organizationId,
        permissions: ['manage_security', 'edit', 'view'],
      });
      setPermissionList(list || { permissions: {} });
    };
    fetchPermissions();
  }, [organizationId, permissionClient]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // ---------- Filtering ----------

  useEffect(() => {
    let filtered = users;

    if (statusFilter.code !== 'all') {
      filtered = filtered.filter((row) => {
        const active = isUserActive(row);
        return statusFilter.code === 'active' ? active : !active;
      });
    }

    if (centerFilter.code !== 'all') {
      filtered = filtered.filter((user) => {
        const centerPersp = findPerspective(user, 'center_center');
        if (!centerPersp) return false;
        const spheres = centerPersp.assignedSpheres ?? centerPersp.assigned?.map((a: any) => a.sphere) ?? [];
        return spheres.some((s: any) => s?.id === centerFilter.code);
      });
    }

    if (searchValue) {
      const lower = searchValue.toLowerCase();
      filtered = filtered.filter((row) => {
        const name = unwrap(row.properties?.name).toLowerCase();
        const identityName = getAssignedSphere(row, 'center_associated_identity')?.properties?.name?.toLowerCase?.() || '';
        return name.includes(lower) || identityName.includes(lower);
      });
    }

    setFilteredUsers(filtered);
  }, [searchValue, users, statusFilter, centerFilter]);

  // Extract unique centers for center filter dropdown
  useEffect(() => {
    const centersMap: Record<string, any> = {};
    users.forEach((user) => {
      const centerPersp = findPerspective(user, 'center_center');
      if (!centerPersp) return;
      const spheres = centerPersp.assignedSpheres ?? centerPersp.assigned?.map((a: any) => a.sphere) ?? [];
      spheres.forEach((s: any) => {
        if (s) centersMap[s.id] = s;
      });
    });
    const unique = Object.values(centersMap);
    setCenterOptions([
      { name: t('usersMolecule.status.all'), code: 'all' },
      ...unique.map((center) => ({
        name: center.properties?.name,
        code: center.id,
      })),
    ]);
  }, [users, t]);

  // ---------- Sort ----------

  const handleSort = useCallback((event: DataTableSortEvent) => {
    const field = event.sortField as string;
    const order = event.sortOrder as 1 | -1;
    setSortField(field);
    setSortOrder(order);

    setFilteredUsers((prev) => {
      const sorted = [...prev];
      if (field === 'identity') {
        sorted.sort((a, b) => {
          const aVal = getAssignedSphere(a, 'center_associated_identity')?.properties?.name?.toLowerCase?.() || '';
          const bVal = getAssignedSphere(b, 'center_associated_identity')?.properties?.name?.toLowerCase?.() || '';
          const greaterResult = aVal > bVal ? 1 * order : 0;
          return aVal < bVal ? -1 * order : greaterResult;
        });
      } else if (field === 'center') {
        sorted.sort((a, b) => {
          const aVal = getAssignedSphere(a, 'center_center')?.properties?.name?.toLowerCase?.() || '';
          const bVal = getAssignedSphere(b, 'center_center')?.properties?.name?.toLowerCase?.() || '';
          const greaterResult = aVal > bVal ? 1 * order : 0;
          return aVal < bVal ? -1 * order : greaterResult;
        });
      } else if (field === 'manager') {
        sorted.sort((a, b) => {
          const aVal = getAssignedSphere(a, 'center_manager')?.properties?.name?.toLowerCase?.() || '';
          const bVal = getAssignedSphere(b, 'center_manager')?.properties?.name?.toLowerCase?.() || '';
          const greaterResult = aVal > bVal ? 1 * order : 0;
          return aVal < bVal ? -1 * order : greaterResult;
        });
      } else if (field === 'role') {
        sorted.sort((a, b) => {
          const aVal = unwrap(a.properties?.user_type) || 'user';
          const bVal = unwrap(b.properties?.user_type) || 'user';
          const greaterResult = aVal > bVal ? 1 * order : 0;
          return aVal < bVal ? -1 * order : greaterResult;
        });
      }
      return sorted;
    });
  }, []);

  // ---------- Perspective assignment ----------

  const handlePerspectiveChange = useCallback(async (
    sphereId: string,
    perspectiveSystemName: string,
    assignee: string | null
  ) => {
    if (!graphClient) return;
    try {
      if (assignee) {
        await graphClient.post(`/spheres/${sphereId}/perspectives/assign-subject`, {
          perspectiveSystemName,
          subjectId: assignee,
        });
      } else {
        await graphClient.delete(`/perspectives/${perspectiveSystemName}/assign-subject`, {
          params: { sphereId, subjectId: assignee },
        });
      }
      // Optimistic update (use context API format)
      setUsers((prev) =>
        prev.map((user) => {
          if (user.id !== sphereId) return user;
          const updatedPerspectives = [...(user.assignedPerspectives || [])];
          const idx = updatedPerspectives.findIndex(
            (p) => p?.perspectiveSystemName === perspectiveSystemName || p?.perspectiveProperties?.system_name === perspectiveSystemName
          );
          if (idx > -1) {
            const updated = { ...updatedPerspectives[idx] };
            if (assignee) {
              const assignedUser = prev.find((u) => u.id === assignee);
              updated.assignedSpheres = [{ id: assignee, properties: assignedUser?.properties || {}, glyph: assignedUser?.glyph, glyphColor: assignedUser?.glyphColor }];
            } else {
              updated.assignedSpheres = [];
            }
            updatedPerspectives[idx] = updated;
          } else if (assignee) {
            const assignedUser = prev.find((u) => u.id === assignee);
            updatedPerspectives.push({
              perspectiveSystemName: perspectiveSystemName,
              assignedSpheres: [{ id: assignee, properties: assignedUser?.properties || {}, glyph: assignedUser?.glyph, glyphColor: assignedUser?.glyphColor }],
            });
          }
          return { ...user, assignedPerspectives: updatedPerspectives };
        })
      );
    } catch (err) {
      console.error('Failed to assign/remove perspective:', err);
    }
  }, [graphClient]);

  // ---------- Dialogs ----------

  const handleEditClick = useCallback((user: User) => {
    setSelectedSphere(user);
    setEditDialogVisible(true);
  }, []);

  const fetchSpherePermissions = useCallback(async (sphereId: string, nodeLabel: string, permissions?: string[]) => {
    if (!permissionClient) return null;
    return fetchPermissionList(permissionClient, {
      resourceType: nodeLabel,
      resourceId: sphereId,
      permissions: permissions ?? [],
    });
  }, [permissionClient]);

  const centerPerspective = selectedSphere ? findPerspective(selectedSphere, 'center_center') : undefined;
  const parentSphere = (centerPerspective?.assignedSpheres?.[0] ?? centerPerspective?.assigned?.[0]?.sphere) as any;

  // ---------- Column definitions ----------

  const columns: DataGridColumn[] = useMemo(() => [
    {
      field: 'properties.name.0',
      header: t('usersMolecule.name'),
      sortable: true,
      minWidth: '140px',
      body: createUserNameBody(handleEditClick),
    },
    {
      field: 'properties.title.0',
      header: t('usersMolecule.title'),
      sortable: true,
      minWidth: '140px',
      body: createTitleBody(),
    },
    {
      field: 'properties.active',
      header: t('usersMolecule.active'),
      minWidth: '100px',
      body: createActiveBody(canManageSecurity, graphClient, fetchUsers),
    },
    {
      field: 'identity',
      header: t('usersMolecule.identity'),
      sortable: true,
      minWidth: '200px',
      body: createIdentityBody(),
    },
    {
      field: 'center',
      header: t('usersMolecule.center'),
      sortable: true,
      minWidth: '300px',
      body: createPerspectiveBody('center_center', organizationId, handlePerspectiveChange),
    },
    {
      field: 'manager',
      header: t('usersMolecule.manager'),
      sortable: true,
      minWidth: '300px',
      body: createPerspectiveBody('center_manager', organizationId, handlePerspectiveChange),
    },
    {
      field: 'role',
      header: t('usersMolecule.role'),
      sortable: true,
      minWidth: '120px',
      body: createRoleBody(t),
    },
  ], [t, canManageSecurity, graphClient, fetchUsers, organizationId, handleEditClick, handlePerspectiveChange]);

  // ---------- Custom filters ----------

  const customFilters = useMemo(() => (
    <div className="user-list-organism__filter-bar">
      <div className="user-list-organism__filter-field">
        <label htmlFor="user-status-filter" className="user-list-organism__filter-label">
          {t('usersMolecule.statusLabel')}
        </label>
        <PrimeReactSingleSelect
          id="user-status-filter"
          value={statusFilter}
          options={statusOptions}
          onChange={(e) => { if (e.value) setStatusFilter(e.value); }}
          placeholder={t('usersMolecule.status.all')}
          className="user-list-organism__status-dropdown"
        />
      </div>
      <div className="user-list-organism__filter-field">
        <label htmlFor="user-center-filter" className="user-list-organism__filter-label">
          {t('usersMolecule.centerLabel')}
        </label>
        <PrimeReactSingleSelect
          id="user-center-filter"
          value={centerFilter}
          options={centerOptions}
          onChange={(e) => { if (e.value) setCenterFilter(e.value); }}
          placeholder={t('usersMolecule.status.all')}
          className="user-list-organism__center-dropdown"
        />
      </div>
    </div>
  ), [statusFilter, centerFilter, statusOptions, centerOptions, t]);

  // ---------- Header actions (invite button) ----------

  const headerActions = useMemo(() => {
    if (!canManageSecurity) return undefined;
    return (
      <ButtonAtom
        variant="primary"
        label={t('usersMolecule.inviteUser')}
        onClick={() => setInviteDialogVisible(true)}
      />
    );
  }, [canManageSecurity, t]);

  // ---------- Load error ----------

  const loadErrorContent = useMemo(() => {
    if (!loadFailed) return undefined;
    return (
      <div role="alert" data-testid="user-list-load-error">
        <EmptyStateAtom
          icon="pi-exclamation-triangle"
          title={t('usersMolecule.loadError.title')}
          description={t('usersMolecule.loadError.description')}
        >
          <ButtonAtom
            variant="secondary"
            icon="pi pi-refresh"
            label={t('usersMolecule.loadError.retry')}
            onClick={() => { void fetchUsers(); }}
          />
        </EmptyStateAtom>
      </div>
    );
  }, [loadFailed, fetchUsers, t]);

  // ---------- Mobile card template ----------

  const mobileCardTemplate = useCallback((item: unknown) => {
    const user = item as User;
    return (
      <MobileUserCard
        key={user.id}
        user={user}
        canToggle={canManageSecurity}
        graphClient={graphClient}
        fetchUsers={fetchUsers}
        onEditClick={handleEditClick}
        organizationId={organizationId}
        onPerspectiveChange={handlePerspectiveChange}
      />
    );
  }, [canManageSecurity, graphClient, fetchUsers, handleEditClick, organizationId, handlePerspectiveChange]);

  // ---------- Render ----------

  if (!apiClients) return null;

  return (
    <div className="user-list-organism">
      <DataListMolecule
        data={filteredUsers}
        columns={columns}
        loading={loading}
        errorContent={loadErrorContent}
        emptyMessage={t('usersMolecule.noUsersFound')}
        dataKey="id"
        title={t('usersMolecule.heading')}
        icon="pi pi-users"
        collapsible={false}
        showSearch
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        searchPlaceholder={t('usersMolecule.searchPlaceholder')}
        showRefresh
        onRefresh={fetchUsers}
        customFilters={customFilters}
        headerActions={headerActions}
        sortField={sortField}
        sortOrder={sortOrder}
        onSort={handleSort}
        paginator
        rows={25}
        rowsPerPageOptions={[25, 50, 100]}
        mobileCardTemplate={mobileCardTemplate}
        mobileBreakpoint={768}
        className="user-list-organism__table"
      />

      <DialogWrapper
        header={t('usersMolecule.editUserSphereDialogTitle')}
        visible={editDialogVisible}
        onHide={() => setEditDialogVisible(false)}
      >
        {selectedSphere && (
          <>
            <RoleEditSection
              sphere={selectedSphere}
              organizationId={organizationId}
              graphClient={graphClient}
              onSaved={fetchUsers}
            />
            <EditSphere
              sphere={selectedSphere}
              parentSphere={parentSphere}
              onSave={() => { setEditDialogVisible(false); fetchUsers(); }}
              onSphereRefresh={handleSphereRefresh}
              onClose={() => setEditDialogVisible(false)}
              fetchPermissionList={fetchSpherePermissions}
            />
          </>
        )}
      </DialogWrapper>

      <InviteUsersDialogMolecule
        visible={inviteDialogVisible}
        onHide={() => setInviteDialogVisible(false)}
        organizationId={organizationId}
        fetchUsers={fetchUsers}
      />
    </div>
  );
};

export default UserListOrganism;
